import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Button, Popconfirm, Popover, Space, Tag, Tooltip } from 'antd';
import { AiOutlineDelete, AiOutlineFileImage } from 'react-icons/ai';
import { Comment } from '@ant-design/compatible';
import { CloseOutlined, UploadOutlined } from '@ant-design/icons';
import { FaQuoteLeft, FaRegSmile } from 'react-icons/fa';
import { MdOutlineTextFormat } from 'react-icons/md';
import { UploadFileApi, UploadResponse, UserResponse } from 'src/types';
import {
  convertSnakeCaseToTitleCase,
  deltaToHtml,
  mentionCommentParser,
  parseDateTimeToUSFormat,
} from 'src/utils/stringHelpers';
import UserAvatarPopover from 'src/components/UserAvatarPopover';
import DownloadAsset from '../DownloadAsset';
import RichTextEditor, { RichTextEditorHandle } from '../RichTextEditor';
import CommentActions, {
  CommentActionsChildrenProps,
  CommentActionsParentProps,
} from '../CommentActions';
import FileUploadModal from '../FileUpload';
import { EmojiPicker } from '../EmojiPicker';
import { PickerPositioner } from '../EmojiPicker/PickerPositioner';

interface CommentMessageBaseProps {
  comment: any;
  user: UserResponse;
  projectUuid: string;
  targetCommentId: any;
  showCommentType?: boolean;
  showActions?: boolean;
  mentionTypes?: ('regular_user' | 'freelancer' | 'contact' | 'group')[];
  mentionableUrl?: string;
  prefetchedMentionables?: any[];
  onImgDelete: any;
  onReplyTo: CommentActionsParentProps['onReplyTo'];
  onQuoteTo: (uuid: string, comment: any) => void;
  onScrollToComment?: (uuid: string) => void;
  children?: ReactNode;
  commentReplies?: any;
}

interface CommentMessageParentProps extends CommentMessageBaseProps {
  parentEl?: undefined;
  onAddReaction: CommentActionsParentProps['onAddReaction'];
  onRemoveReaction: CommentActionsParentProps['onRemoveReaction'];
  onUpdate: CommentActionsParentProps['onUpdate'];
  onDelete: CommentActionsParentProps['onDelete'];
}

interface CommentMessageChildrenProps extends CommentMessageBaseProps {
  parentEl: CommentActionsChildrenProps['parentEl'];
  onAddReaction: CommentActionsChildrenProps['onAddReaction'];
  onRemoveReaction: CommentActionsChildrenProps['onRemoveReaction'];
  onUpdate: CommentActionsChildrenProps['onUpdate'];
  onDelete: CommentActionsChildrenProps['onDelete'];
}

export type CommentMessageProps =
  | CommentMessageParentProps
  | CommentMessageChildrenProps;

const DELETE_MESSAGE = '[Comment deleted]';

const CommentMessage: FC<CommentMessageProps> = (props) => {
  const {
    comment,
    commentReplies,
    user,
    projectUuid,
    showActions,
    showCommentType,
    targetCommentId,
    mentionTypes,
    mentionableUrl,
    prefetchedMentionables,
    onImgDelete,
    onReplyTo,
    onQuoteTo,
    onScrollToComment,
  } = props;

  const [editing, setEditing] = useState(false);
  const [editingText, setEditingText] = useState('');
  const [quotedRemoved, setQuotedRemoved] = useState(false);
  const [images, setImages] = useState<UploadFileApi[]>([]);
  const [loadingImages, setLoadingImages] = useState(0);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [showEditorToolbar, setShowEditorToolbar] = useState(false);
  const [isEditorMultiline, setIsEditorMultiline] = useState(false);
  const singleLineHeightRef = useRef(38);

  const [emojiSearch, setEmojiSearch] = useState<{ active: boolean; query: string; index: number }>({
    active: false,
    query: '',
    index: 0,
  });
  const [popoverCoords, setPopoverCoords] = useState({ top: 0, left: 0 });
  const [hasEmojiResults, setHasEmojiResults] = useState(false);

  const commentRef = useRef<RichTextEditorHandle>(null);

  const bgTargetComment =
    targetCommentId === comment.uuid ? '#e2e2e2' : undefined;

  const parsedText = comment.text_delta
    ? deltaToHtml(comment.text_delta)
    : comment.text;

  const editorValue = editing ? editingText : mentionCommentParser(parsedText);

  useEffect(() => {
    if (!editing) {
      setEditingText('');
      setQuotedRemoved(false);
      setImages([]);
      setLoadingImages(0);
      setUploadModalVisible(false);
      setShowEditorToolbar(false);
      setIsEditorMultiline(false);
      setEmojiSearch({ active: false, query: '', index: 0 });
      setHasEmojiResults(false);
    }
  }, [editing]);

  useEffect(() => {
    if (!editing) return;
    const timer = setTimeout(() => {
      const editorEl = commentRef.current?.getQuill()?.getEditor()?.root;
      if (!editorEl) return;
      const cs = window.getComputedStyle(editorEl);
      const lineHeight = parseFloat(cs.lineHeight) || 23;
      const paddingTop = parseFloat(cs.paddingTop) || 5;
      const paddingBottom = parseFloat(cs.paddingBottom) || 5;
      singleLineHeightRef.current = lineHeight + paddingTop + paddingBottom + 4;
      setIsEditorMultiline(editorEl.scrollHeight > singleLineHeightRef.current);
    }, 0);
    return () => clearTimeout(timer);
  }, [editing, showEditorToolbar]);

  const handleAddImages = (uploads: UploadFileApi[]) => {
    setImages((prev) => [...prev, ...uploads]);
    setUploadModalVisible(false);
  };

  const handleEmojiSelect = (emoji: { id: string; native: string }) => {
    const quill = commentRef.current?.getQuill()?.getEditor();
    if (quill) {
      let { index } = quill.getSelection(true);

      if (emojiSearch.active) {
        const lengthToRemove = emojiSearch.query.length + 1;
        quill.deleteText(emojiSearch.index, lengthToRemove);
        index = emojiSearch.index;
      }

      quill.insertEmbed(index, 'emoji-inline', { id: emoji.id, native: emoji.native });
      quill.setSelection(index + 1, 0);
      setEmojiSearch((prev) => ({ ...prev, active: false }));
    }
  };

  const emojiPickerContent = (
    <div ref={(ref) => {
      if (ref) ref.onclick = (e) => e.stopPropagation();
    }}>
      <EmojiPicker onSelect={handleEmojiSelect} />
    </div>
  );

  let actions;

  if (!comment.deleted_at && showActions) {
    actions = props.parentEl ? (
      <CommentActions
        key="actions"
        comment={comment}
        user={user}
        images={images}
        loadingImages={loadingImages}
        editing={editing}
        quotedRemoved={quotedRemoved}
        editingText={editingText}
        editorText={commentRef}
        parentEl={props.parentEl}
        onEdit={(edit) => {
          setEditing(edit);
          setEditingText(mentionCommentParser(parsedText));
        }}
        onReplyTo={onReplyTo}
        onQuoteTo={onQuoteTo}
        onAddReaction={props.onAddReaction}
        onRemoveReaction={props.onRemoveReaction}
        onUpdate={props.onUpdate}
        onDelete={props.onDelete}
      />
    ) : (
      <CommentActions
        key="actions"
        comment={comment}
        user={user}
        images={images}
        loadingImages={loadingImages}
        editing={editing}
        quotedRemoved={quotedRemoved}
        editingText={editingText}
        editorText={commentRef}
        onEdit={(edit) => {
          setEditing(edit);
          setEditingText(mentionCommentParser(parsedText));
        }}
        onReplyTo={onReplyTo}
        onQuoteTo={onQuoteTo}
        onAddReaction={props.onAddReaction}
        onRemoveReaction={props.onRemoveReaction}
        onUpdate={props.onUpdate}
        onDelete={props.onDelete}
      />
    );
  }

  return (
    <>
      <div
        className={`relative mr-[15px] rounded-md px-[10px] comment-${comment.uuid} comment-wrapper group`}
        style={{
          background: bgTargetComment ? '#f0f0f0' : undefined,
          borderRadius: 5,
          marginInlineEnd: props.parentEl ? '10px' : undefined,
        }}
      >
        <Comment
          className="!bg-transparent"
          author={
            <>
              <Space style={{ minHeight: 22 }}>
                <p className="text-gray-600">{comment.creator?.name}</p>
                <p className="text-gray-400">
                  {parseDateTimeToUSFormat(comment.created_at)}
                </p>

                {!props.parentEl && showCommentType && (
                  <Tag color={comment.type === 'private' ? 'gold' : 'blue'}>
                    {convertSnakeCaseToTitleCase(comment.type)}
                  </Tag>
                )}
              </Space>

              {comment.creator?.job_title ? (
                <div className="mb-1 leading-none">
                  {comment.creator?.job_title}
                </div>
              ) : <div style={{ marginBottom: 16 }} /> }
            </>
          }
          avatar={<UserAvatarPopover user={comment.creator} />}
          content={
            <div>
              {editorValue.toLowerCase() !== DELETE_MESSAGE.toLowerCase() && comment.quoted && !editing && (
                <div
                  className={`quoted-comment ${comment.deleted_at && 'deleted-quoted-comment' }`}
                  title="Jump to original message"
                  onClick={() => onScrollToComment?.(comment.quoted.uuid)}
                >
                  <div className="quoted-line" />
                  <div className="quoted-content">
                    <div className='flex items-center'>
                      <FaQuoteLeft size={10} style={{ marginRight: 5, opacity: 0.5 }} />
                      <strong className='!mb-0'>{comment.quoted.creator?.name}</strong>
                    </div>
                    <RichTextEditor
                      key={comment.quoted.uuid}
                      className="comment-styles resume"
                      defaultValue={mentionCommentParser(
                        comment.quoted.text_delta
                          ? deltaToHtml(comment.quoted.text_delta)
                          : comment.quoted.text,
                      )}
                      readonly
                      noContainer
                    />
                  </div>
                </div>
              )}

              <div className={editing ? 'chatInputWrapper comment-edit-wrapper' : undefined}>
                {editing && (
                  <FileUploadModal
                    visible={uploadModalVisible}
                    onOk={handleAddImages}
                    closeUploadModal={() => setUploadModalVisible(false)}
                  />
                )}
                {comment.quoted && editing && !quotedRemoved && (
                  <div className="replyTo" style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <div className="line" />
                    <div style={{ flex: 1 }}>
                      <div className='flex items-center'>
                        <FaQuoteLeft size={10} style={{ marginRight: 5, opacity: 0.5 }} />
                        <strong className='!mb-0'>{comment.quoted.creator?.name}</strong>
                      </div>
                      <RichTextEditor
                        key={`edit-quoted-${comment.quoted.uuid}`}
                        className="comment-styles resume"
                        defaultValue={mentionCommentParser(
                          comment.quoted.text_delta
                            ? deltaToHtml(comment.quoted.text_delta)
                            : comment.quoted.text
                        )}
                        readonly
                        noContainer
                      />
                    </div>
                    <CloseOutlined
                      style={{ fontSize: 12, opacity: 0.5, cursor: 'pointer', flexShrink: 0, paddingTop: 2 }}
                      onClick={() => setQuotedRemoved(true)}
                    />
                  </div>
                )}
                <div className={editing ? `chatInputEditorRow${isEditorMultiline ? ' chatInputEditorRowMultiline' : ''}` : undefined}>
                  <RichTextEditor
                    ref={commentRef}
                    value={editorValue}
                    className={`${editing ? `chat-input${!showEditorToolbar ? ' no-toolbar' : ''}` : 'comment-styles'}${
                      comment.deleted_at ? ' deleted-message' : ''
                    }`}
                    placeholder="Add a comment"
                    projectId={projectUuid}
                    mentionableUrl={mentionableUrl}
                    prefetchedMentionables={prefetchedMentionables}
                    includeToolbar={editing ? true : false}
                    readonly={!editing}
                    mentionTypes={mentionTypes}
                    noContainer={!editing}
                    includeMention={editing}
                    onImageUploader={async (upload) => { setImages((prev) => [...prev, upload]); }}
                    onLoadingImage={(value) => { if (editing) setLoadingImages(value); }}
                    onChange={(content, delta, source, editor) => {
                      setEditingText(content);
                      if (editing) {
                        const isEmpty = !content || content === '<p><br></p>';
                        const scrollHeight = commentRef.current?.getQuill()?.getEditor()?.root?.scrollHeight ?? 0;
                        setIsEditorMultiline((prev) => {
                          if (isEmpty) return false;
                          if (prev) return true;
                          return scrollHeight > singleLineHeightRef.current + 5;
                        });
                      }
                      if (source === 'user' && editing) {
                        const ops = (delta as any).ops || [];
                        const lastOp = ops[ops.length - 1];
                        const quill = editor;

                        if (lastOp?.insert === ':') {
                          const range = quill.getSelection();
                          if (range) {
                            const colonIndex = range.index - 1;
                            const charBefore = colonIndex > 0 ? quill.getText(colonIndex - 1, 1) : '';
                            const ALLOWED_BEFORE_COLON = /^(\s|\uFEFF|\uFFFC|)$/;

                            if (ALLOWED_BEFORE_COLON.test(charBefore)) {
                              const quillInstance = commentRef.current?.getQuill()?.getEditor();
                              const containerRect = (quillInstance as any)?.container?.getBoundingClientRect();
                              const bounds = quill.getBounds(range.index);
                              if (containerRect) {
                                setPopoverCoords({
                                  top: containerRect.top + bounds.top,
                                  left: containerRect.left + bounds.left,
                                });
                              }
                              setEmojiSearch({ active: true, query: '', index: colonIndex });
                              setHasEmojiResults(true);
                            }
                          }
                        } else if (emojiSearch.active) {
                          const currentRange = quill.getSelection();
                          if (currentRange) {
                            if (currentRange.index <= emojiSearch.index) {
                              setEmojiSearch((prev) => ({ ...prev, active: false }));
                              setHasEmojiResults(false);
                            } else {
                              const textSinceColon = quill.getText(
                                emojiSearch.index + 1,
                                currentRange.index - (emojiSearch.index + 1)
                              );
                              if (textSinceColon.includes(' ') || textSinceColon.includes('\n')) {
                                setEmojiSearch((prev) => ({ ...prev, active: false }));
                                setHasEmojiResults(false);
                              } else {
                                setEmojiSearch((prev) => ({ ...prev, query: textSinceColon }));
                              }
                            }
                          }
                        }
                      }
                    }}
                  />

                  {editing && (
                    <div className="chatInputActions">
                      <span>
                        <Tooltip title="Formatting options">
                          <Button
                            type="text"
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            icon={<MdOutlineTextFormat style={{ fontSize: '18px' }} />}
                            onClick={() => setShowEditorToolbar(!showEditorToolbar)}
                          />
                        </Tooltip>
                      </span>
                      <Tooltip title="Add emoji">
                        <Popover
                          content={emojiPickerContent}
                          trigger="click"
                          placement="bottomLeft"
                          getPopupContainer={() => document.body}
                          overlayClassName="no-padding-popover"
                        >
                          <Button
                            type="text"
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            icon={<FaRegSmile style={{ fontSize: '18px' }} />}
                          />
                        </Popover>
                      </Tooltip>
                      <Tooltip title="Upload file">
                        <Button
                          type="text"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          icon={<UploadOutlined style={{ fontSize: '18px' }} />}
                          onClick={() => setUploadModalVisible(true)}
                        />
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>

              {(comment.uploads?.length > 0 || (editing && images.length > 0)) && !comment.deleted_at && (
                <Space size={10} direction="vertical" className="w-full mt-3">
                  {comment.uploads?.map((file: UploadResponse) => {
                    return (
                      <div
                        style={{
                          background: '#F0F0F0',
                          padding: 5,
                          borderRadius: 3,
                          paddingLeft: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                        key={file.uuid}
                      >
                        <a
                          href={`/file-viewer/${file.uuid}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2"
                        >
                          <AiOutlineFileImage />
                          <span>{file.name}</span>
                        </a>

                        <Space size={1}>
                          <DownloadAsset uuid={file.uuid} name={file.name} />

                          {!comment.deleted_at &&
                            comment.creator.uuid === user.uuid &&
                            showActions && (
                              <Popconfirm
                                placement="bottom"
                                title="Do you want to delete this image?"
                                onConfirm={() =>
                                  onImgDelete(file.uuid, comment.uuid)
                                }
                                okText="Yes"
                                cancelText="No"
                              >
                                <Button
                                  type="link"
                                  icon={
                                    <AiOutlineDelete className="text-red-500" />
                                  }
                                />
                              </Popconfirm>
                            )}
                        </Space>
                      </div>
                    );
                  })}

                  {editing && images.map((el) => (
                    <div
                      key={el.response?.uuid ?? el.uid}
                      style={{
                        background: '#F0F0F0',
                        padding: 5,
                        borderRadius: 3,
                        paddingLeft: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span className="flex items-center gap-2">
                        <AiOutlineFileImage />
                        <span>{el.name}</span>
                      </span>
                      <Button
                        type="link"
                        icon={<CloseOutlined className="text-red-500" />}
                        onClick={() => setImages((prev) => prev.filter((img) => img.response?.uuid !== el.response?.uuid))}
                      />
                    </div>
                  ))}
                </Space>
              )}
              {actions}
            </div>
          }
        />
      </div>

      {commentReplies && !props.parentEl && (
        <div className="mt-[6px] pl-10">{commentReplies}</div>
      )}

      {editing && emojiSearch.active && (
        <PickerPositioner top={popoverCoords.top} left={popoverCoords.left} visible={hasEmojiResults}>
          <EmojiPicker
            onSelect={handleEmojiSelect}
            searchQuery={emojiSearch.query}
            inline
            onResultsCountChange={(count) => setHasEmojiResults(count > 0)}
            onClose={() => {
              setEmojiSearch((prev) => ({ ...prev, active: false }));
              setHasEmojiResults(false);
            }}
          />
        </PickerPositioner>
      )}
    </>
  );
};

export default CommentMessage;
